import { useChat } from "@ai-sdk/react";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { ChatComposer } from "@repo/ui/chat/chatComposer";
import {
  ChatMessage,
  type ChatMessageRenderTextArgs,
} from "@repo/ui/chat/chatMessage";
import { ChatThread } from "@repo/ui/chat/chatThread";
import { ChatToolBase, type ToolComponent } from "@repo/ui/chat/chatToolCall";
import { MessageItem } from "@repo/ui/chat/messageItem";
import { Size } from "@repo/ui/helpers/helpers";
import * as Sentry from "@sentry/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  apiBaseUrl,
  getApiAuthorizationHeaderValue,
} from "../../api/shared.js";

interface TeammateAgentSimulationProps {
  teammateAgentId: TeammateId;
  shouldUseLiveToolCalls: boolean;
}

/**
 * "Try this agent": a text conversation with a teammate voice agent. The
 * conversation lives here and nowhere else — every turn sends it back to the
 * agent's simulation route — so remounting starts over.
 */
export const TeammateAgentSimulation: FC<TeammateAgentSimulationProps> = (
  props,
) => {
  const { teammateAgentId, shouldUseLiveToolCalls } = props;
  // One id for the whole conversation: the server groups its turns under it.
  const [conversationId] = useState(() => uuidv4());

  // Read at send time so toggling live tool calls does not rebuild the chat.
  const shouldUseLiveToolCallsRef = useRef(shouldUseLiveToolCalls);
  shouldUseLiveToolCallsRef.current = shouldUseLiveToolCalls;

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: new URL(
          `/agents/${encodeURIComponent(teammateAgentId)}/simulation`,
          apiBaseUrl,
        ).toString(),
        prepareSendMessagesRequest: async ({ messages }) => ({
          headers: {
            Authorization: await getApiAuthorizationHeaderValue(),
            "x-alpharun-commit-sha": __COMMIT_SHA__,
          },
          body: {
            conversation_id: conversationId,
            messages,
            use_live_tool_calls: shouldUseLiveToolCallsRef.current,
          },
        }),
      }),
    [teammateAgentId, conversationId],
  );

  const setMessagesRef = useRef<
    ((updater: (messages: UIMessage[]) => UIMessage[]) => void) | null
  >(null);
  const { messages, sendMessage, status, stop, setMessages } =
    useChat<UIMessage>({
      transport,
      // AI SDK only flips `status` on a failure; show it in the thread.
      onError: (error) => {
        Sentry.captureException(error);
        setMessagesRef.current?.((prev) => [
          ...prev,
          {
            id: `error-${uuidv4()}`,
            role: "assistant",
            parts: [
              { type: "text", text: `Something went wrong: ${error.message}` },
            ],
          },
        ]);
      },
    });
  setMessagesRef.current = setMessages;

  // The agent opens the conversation, once (StrictMode mounts twice).
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    if (!hasOpenedRef.current) {
      hasOpenedRef.current = true;
      void sendMessage();
    }
  }, [sendMessage]);

  const isRunning = status === "submitted" || status === "streaming";
  const lastMessageId = messages.at(-1)?.id;

  return (
    <ChatThread
      className="-m-4 flex-auto"
      messages={messages}
      isLoading={status === "submitted"}
      renderMessage={(message) => (
        <ChatMessage
          message={message}
          isStreaming={isRunning && message.id === lastMessageId}
          tools={{ Fallback: SimulatedToolCall }}
          renderText={renderSimulationText}
        />
      )}
      composer={
        <div className="border-t border-border-light bg-white p-4">
          <ChatComposer
            size={Size.SMALL}
            placeholder="Say something to the agent"
            isRunning={isRunning}
            onStop={stop}
            onSend={(text) => {
              void sendMessage({ text });
            }}
          />
        </div>
      }
    />
  );
};

function renderSimulationText({ text, role }: ChatMessageRenderTextArgs) {
  if (!text) {
    return null;
  }
  return (
    <MessageItem role={role === "user" ? "user" : "assistant"}>
      {text}
    </MessageItem>
  );
}

/** What the simulated tool produced, as the runtime shapes it. */
interface SimulatedToolOutput {
  content?: string;
  alternativeContent?: string;
}

const SimulatedToolCall: ToolComponent<unknown, SimulatedToolOutput> = ({
  toolName,
  output,
  errorText,
  status,
}) => (
  <ChatToolBase status={status}>
    {errorText ??
      output?.alternativeContent ??
      output?.content ??
      `Calling ${toolName}`}
  </ChatToolBase>
);
